import { useState } from "react";
import { useNavigate } from "react-router";
import { AuthService } from "@/hooks/services/authService";
import {
  Mail, Lock, ArrowRight, CheckCircle2, AlertCircle,
  Eye, EyeOff, School, ChevronUp, FlaskConical,
} from "lucide-react";
import { AuthStore } from "@/hooks/stores/AuthStore";
import config from "../../../config";

const DEV_USERS = [
  { label: "School Admin", email: "admin@greenwood.com", password: "Admin1234!", color: "from-teal-500 to-green-600", initials: "SA" },
];

const DevUserSelector = ({ onSelect }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
      {open && (
        <div className="bg-white/90 backdrop-blur-md border border-slate-200 rounded-2xl shadow-2xl p-3 w-60 space-y-2 animate-in slide-in-from-bottom-2 duration-200">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-1 pb-1 border-b border-slate-100">Quick login</p>
          {DEV_USERS.map((u) => (
            <button key={u.email} onClick={() => { onSelect(u); setOpen(false); }}
              className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors text-left">
              <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${u.color} flex items-center justify-center text-white text-xs font-bold shrink-0`}>
                {u.initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">{u.label}</p>
                <p className="text-xs text-slate-400 truncate">{u.email}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      <button onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold px-3 py-2 rounded-full shadow-lg transition-all">
        <FlaskConical className="w-3.5 h-3.5" />
        Dev
        <ChevronUp className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "" : "rotate-180"}`} />
      </button>
    </div>
  );
};

export default function SchoolSignInPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const { user, accessToken, refreshToken } = await AuthService.login({
        email: formData.email,
        password: formData.password,
        userType: "school_admin",
      });
      AuthStore.getState().login(true, user.userType, user, accessToken, refreshToken);
      setSuccess(true);
      setTimeout(() => navigate("/school-dashboard"), 1200);
    } catch (err) {
      setError(err?.response?.data?.error?.message || err.message || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/20 overflow-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-40 right-10 w-96 h-96 bg-teal-200/20 rounded-full blur-3xl" />
      </div>

      <div className="w-full flex flex-col lg:flex-row relative z-10">
        {/* Image panel */}
        <div className="hidden lg:flex w-1/2 items-center justify-center p-10">
          <div className="relative w-full max-w-md">
            <img src="/schools.avif" alt="School" className="w-full h-auto rounded-3xl shadow-2xl" />
            <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-2 rounded-full font-bold text-base shadow-lg">
              School Portal
            </div>
          </div>
        </div>

        {/* Form panel */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-md space-y-7">
            <button onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 font-medium transition-colors">
              <School className="w-4 h-4" />
              School Portal
              <span className="text-slate-300">·</span>
              <span className="text-emerald-600 hover:underline">Change portal</span>
            </button>

            <div className="space-y-1">
              <h1 className="text-3xl font-bold text-slate-900">School sign in</h1>
              <p className="text-slate-500 text-sm">Access your school admin dashboard</p>
            </div>

            <div className="backdrop-blur-xl bg-white/50 border border-white/70 rounded-3xl p-8 sm:p-10 shadow-2xl">
              {success && (
                <div className="mb-6 p-4 bg-green-50/80 border border-green-200/50 rounded-xl flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                  <span className="text-green-800 font-medium text-sm">Signing in…</span>
                </div>
              )}
              {error && !success && (
                <div className="mb-6 p-4 bg-red-50/80 border border-red-200/50 rounded-xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
                  <span className="text-red-800 font-medium text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">School Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="email" name="email" placeholder="admin@school.com"
                      value={formData.email} onChange={handleChange} disabled={isLoading || success}
                      className="w-full pl-11 pr-4 py-3.5 bg-white/70 border border-slate-200/80 rounded-xl focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 text-sm disabled:opacity-50" />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-semibold text-slate-700">Password</label>
                    <a href="/forgot-password" className="text-xs text-emerald-600 hover:text-emerald-700 font-medium">Forgot password?</a>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type={showPassword ? "text" : "password"} name="password" placeholder="••••••••"
                      value={formData.password} onChange={handleChange} disabled={isLoading || success}
                      className="w-full pl-11 pr-11 py-3.5 bg-white/70 border border-slate-200/80 rounded-xl focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 text-sm disabled:opacity-50" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={isLoading || success}
                  className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-semibold text-sm shadow-lg transition-all disabled:opacity-50">
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Signing In…
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Sign In <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </button>
              </form>

              <div className="my-6 flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200/60" />
                <span className="text-xs text-slate-400 font-medium">Not registered yet?</span>
                <div className="flex-1 h-px bg-slate-200/60" />
              </div>

              <button type="button" onClick={() => navigate("/school/signup")}
                className="w-full py-3.5 px-4 bg-white/50 hover:bg-white/80 border border-slate-200/70 rounded-xl text-slate-700 font-semibold text-sm transition-all text-center">
                Register Your School
              </button>
            </div>
          </div>
        </div>
      </div>

      {(config.appMode === "prototype" || config.env === "development") && (
        <DevUserSelector onSelect={(u) => setFormData({ email: u.email, password: u.password })} />
      )}
    </main>
  );
}
